import type { Flow, MissionCardDTO, RiskLevel } from "@bgreen/shared";
import { Link } from "react-router-dom";
import { cn } from "../lib/cn";
import { fmtMoney, fmtTokens, relTime } from "../lib/format";
import { FLOW_LABEL, INDICATOR_LABEL, PRIORITY_LABEL, RISK_LABEL } from "../lib/labels";
import { ThinkingOrb, type OrbState } from "./ThinkingOrb";
import { Chip, Dot } from "./ui/primitives";

export const RiskBadge = ({ level }: { level: RiskLevel }) => (
  <Chip tone={level === "minimal" ? "good" : level === "standard" ? "info" : "bad"}>Riesgo {RISK_LABEL[level].toLowerCase()}</Chip>
);
export const FlowBadge = ({ flow }: { flow: Flow | null }) =>
  flow ? <Chip tone={flow === "fast_track" ? "good" : flow === "supervised" ? "accent" : "bad"} className="font-mono text-[12px]">{FLOW_LABEL[flow]}</Chip> : null;

export const prioColor = (p: string) => (p === "high" ? "er" : p === "medium" ? "wn" : "mu");
export const PriorityTag = ({ p }: { p: string }) => (
  <span className="flex items-center gap-1.5 text-[12px] text-mu"><Dot color={prioColor(p)} />{PRIORITY_LABEL[p as "high"]}</span>
);

/** Color del punto del agente: error/bloqueo > aprobación > en ejecución > terminada > en cola. */
export function agentDot(m: Pick<MissionCardDTO, "status" | "agentRunning" | "indicator">): string {
  if (["error", "blocked", "awaiting_budget"].includes(m.status)) return "er";
  if (m.indicator === "awaiting_plan_approval" || m.indicator === "awaiting_delivery_approval") return "wn";
  if (m.agentRunning) return "gd";
  if (m.status === "done" || m.status === "cancelled") return "mu";
  return "in";
}

/** Estado del orbe del agente; null = sin actividad que mostrar (en cola, terminada, cancelada) y se mantiene el punto. */
export function agentOrb(m: Pick<MissionCardDTO, "status" | "agentRunning" | "indicator">): OrbState | null {
  if (["error", "blocked", "awaiting_budget"].includes(m.status)) return "error";
  if (m.indicator === "awaiting_plan_approval" || m.indicator === "awaiting_delivery_approval") return "approval";
  if (m.status === "awaiting_ci" || m.status === "merging") return "ci";
  if (m.agentRunning && m.status === "planning") return "planning";
  if (m.agentRunning && m.status === "developing") return "developing";
  return null;
}

/** Orbe si el agente tiene actividad; si no, el punto de siempre. */
export function AgentMark({ m, size = 14 }: { m: Pick<MissionCardDTO, "status" | "agentRunning" | "indicator">; size?: number }) {
  const o = agentOrb(m);
  return o ? <ThinkingOrb state={o} size={size} /> : <Dot color={agentDot(m)} pulse={m.agentRunning} />;
}

export function attentionHead(c: MissionCardDTO): { head: string; cta: string; color: string } {
  switch (c.needsAttention) {
    case "plan": return { head: `${c.companyName} · ${c.projectName}: plan pendiente de aprobación`, cta: "Aprobar", color: "wn" };
    case "delivery": return { head: `${c.companyName} · ${c.projectName}: entrega pendiente de aprobación`, cta: "Revisar", color: "in" };
    case "error": return { head: `${c.companyName} · ${c.projectName}: el agente falló`, cta: "Resolver", color: "er" };
    default: return { head: `${c.companyName} · ${c.projectName}: ${c.status === "awaiting_budget" ? "presupuesto excedido" : "misión bloqueada"}`, cta: "Resolver", color: "er" };
  }
}

/** Fila de «Requiere tu atención»: 64 px de alto, un solo toque. */
export function AttentionRow({ c }: { c: MissionCardDTO }) {
  const a = attentionHead(c);
  return (
    <Link to={`/missions/${c.id}`} className="flex min-h-16 items-center gap-3.5 border-t border-ln px-4 py-3 first:border-t-0 hover:bg-hv">
      <Dot color={a.color} className="h-2.5 w-2.5" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[15px] font-semibold leading-snug">{a.head}</span>
        <span className="flex min-w-0 items-center gap-2 text-[14px] text-mu"><span className="shrink-0 font-mono text-[12px]">{c.key}</span><span className="truncate">{c.blockedReason ?? c.title}</span>{c.flow === "restricted" && <span className="shrink-0 rounded border border-er/60 px-1.5 font-mono text-[10px] text-er">RESTRICTED</span>}</span>
      </span>
      <span className="shrink-0 whitespace-nowrap font-mono text-[13px] text-ok"><span className="max-sm:hidden">{a.cta} </span>→</span>
    </Link>
  );
}

export function MissionCard({ c, draggable, onDragStart, onDragEnd, pending, rejected }: {
  c: MissionCardDTO; draggable?: boolean; onDragStart?: (e: React.DragEvent) => void; onDragEnd?: () => void; pending?: boolean; rejected?: string;
}) {
  return (
    <Link to={`/missions/${c.id}`} draggable={draggable} onDragStart={onDragStart} onDragEnd={onDragEnd}
      className={cn("flex flex-col gap-2 rounded-card border bg-cd p-3 transition-colors hover:border-ln2", rejected ? "border-er" : pending ? "border-in opacity-55" : c.indicator === "blocked" || c.indicator === "error" ? "border-ln2" : "border-ln", draggable && "cursor-grab")}>
      <div className="flex items-center justify-between"><span className="font-mono text-[12px] text-mu">{c.key}</span><PriorityTag p={c.priority} /></div>
      <div className="text-[15px] font-medium leading-snug">{c.title}</div>
      <div className="font-mono text-[11px] text-mu">{c.companyName} / {c.projectName} · {c.repositoryName.split("/")[1]}</div>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-[13px]"><AgentMark m={c} size={24} />{c.agentLabel}</span>
        <FlowTag flow={c.flow} risk={c.riskLevel} />
      </div>
      {c.indicator && ["blocked", "error", "cancelled", "awaiting_budget"].includes(c.indicator) && <div className="text-[13px] leading-snug text-er">⊘ {INDICATOR_LABEL[c.indicator]}{c.blockedReason ? `: ${c.blockedReason.slice(0, 90)}` : ""}</div>}
      {(c.indicator === "awaiting_plan_approval" || c.indicator === "awaiting_delivery_approval") && <div className="text-[13px] text-wn">● {INDICATOR_LABEL[c.indicator]}</div>}
      {c.indicator === "awaiting_ci" && <div className="font-mono text-[12px] text-in">⟳ {INDICATOR_LABEL.awaiting_ci}</div>}
      {pending && <div className="font-mono text-[12px] text-in">⟳ Validando con el backend…</div>}
      {rejected && <div className="text-[13px] leading-snug text-er">↩ Rechazado: {rejected}</div>}
      <div className="flex justify-between border-t border-ln pt-2 text-[12px] text-mu">
        <span>{relTime(c.updatedAt)}{c.prNumber ? ` · PR #${c.prNumber}` : ""}</span>
        <span className="font-mono" title="Tokens medidos; el importe solo aparece con precios configurados">{c.totalTokens ? `${fmtTokens(c.totalTokens)} tok` : "—"}{c.estimatedCost ? ` · ${fmtMoney(c.estimatedCost)}` : ""}</span>
      </div>
    </Link>
  );
}

const FlowTag = ({ flow, risk }: { flow: Flow | null; risk: RiskLevel }) => (
  <span className={cn("rounded border px-1.5 py-px font-mono text-[10px]", flow === "restricted" ? "border-er/60 text-er" : flow === "fast_track" ? "border-gd/50 text-gd" : "border-ln2 text-mu")} title={`Riesgo ${RISK_LABEL[risk]}`}>
    {flow ? FLOW_LABEL[flow] : "—"}
  </span>
);
